/**
 * Application constants
 */

// API Configuration
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://vikas.run.place';

// API Endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/api/v1/auth/login',
    REGISTER: '/api/v1/auth/register',
    LOGOUT: '/api/v1/auth/logout',
    REFRESH: '/api/v1/auth/refresh',
    ME: '/api/v1/auth/me',
    CHANGE_PASSWORD: '/api/v1/auth/change-password',
    FORGOT_PASSWORD: '/api/v1/auth/forgot-password',
    RESET_PASSWORD: '/api/v1/auth/reset-password',
  },
};

// Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'authToken',
  REFRESH_TOKEN: 'refreshToken',
  USER_DATA: 'userData',
};

// User Roles
export const USER_ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  // Combined operational role: union of Sales + Account + Delivery access,
  // plus customer create/edit. Warehouse-scoped like the operational roles.
  OPERATIONS_MANAGER: 'OPERATIONS_MANAGER',
  SALES_STAFF: 'SALES_STAFF',
  DELIVERY_SUPERVISOR: 'DELIVERY_SUPERVISOR',
  ACCOUNT_USER: 'ACCOUNT_USER',
  LABOUR: 'LABOUR',
};

// Permissions
export const PERMISSIONS = {
  // User Management
  VIEW_USERS: 'view_users',
  CREATE_USERS: 'create_users',
  EDIT_USERS: 'edit_users',
  DELETE_USERS: 'delete_users',

  // Dashboard
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_ANALYTICS: 'view_analytics',

  // Inventory
  VIEW_INVENTORY: 'view_inventory',
  MANAGE_INVENTORY: 'manage_inventory',

  // Orders
  VIEW_ORDERS: 'view_orders',
  MANAGE_ORDERS: 'manage_orders',

  // Purchase
  VIEW_PURCHASE: 'view_purchase',
  MANAGE_PURCHASE: 'manage_purchase',
  VIEW_PURCHASE_DAYBOOK: 'view_purchase_daybook',

  // Reports
  VIEW_REPORTS: 'view_reports',
  EXPORT_REPORTS: 'export_reports',

  // Settings
  VIEW_SETTINGS: 'view_settings',
  MANAGE_SETTINGS: 'manage_settings',

  // System Administration
  VIEW_SYSTEM_LOGS: 'view_system_logs',
  MANAGE_SYSTEM: 'manage_system',

  // IP Address Management
  VIEW_IP_ADDRESSES: 'view_ip_addresses',
  CREATE_IP_ADDRESSES: 'create_ip_addresses',
  EDIT_IP_ADDRESSES: 'edit_ip_addresses',
  DELETE_IP_ADDRESSES: 'delete_ip_addresses',
  MANAGE_IP_ADDRESSES: 'manage_ip_addresses',

  // Master Data
  VIEW_CUSTOMERS: 'view_customers',
  VIEW_INQUIRIES: 'view_inquiries',
  VIEW_PRODUCTS: 'view_products',
  VIEW_WAREHOUSES: 'view_warehouses',
  VIEW_LOCATIONS: 'view_locations',
  VIEW_TAXES: 'view_taxes',
  VIEW_HSN_CODES: 'view_hsn_codes',
  VIEW_PURCHASE_ADVICE: 'view_purchase_advice',
  VIEW_PURCHASE_CHALLAN: 'view_purchase_challan',

  // AI Advisor (ADMIN / SUPER_ADMIN only — both roles get all permissions below)
  VIEW_AI_ADVISOR: 'view_ai_advisor',
};

// Role Permissions Mapping
export const ROLE_PERMISSIONS = {
  [USER_ROLES.SUPER_ADMIN]: [
    // Super Admin has all permissions
    ...Object.values(PERMISSIONS),
  ],

  [USER_ROLES.ADMIN]: [
    // Admin has all permissions
    ...Object.values(PERMISSIONS),
  ],

  [USER_ROLES.SALES_STAFF]: [
    PERMISSIONS.VIEW_ORDERS,
    PERMISSIONS.MANAGE_ORDERS,
    PERMISSIONS.VIEW_PURCHASE,
    PERMISSIONS.VIEW_INQUIRIES,
    PERMISSIONS.VIEW_PURCHASE_ADVICE,
    PERMISSIONS.VIEW_PURCHASE_CHALLAN,
    PERMISSIONS.VIEW_PURCHASE_DAYBOOK,
    // Read-only inventory (no MANAGE_INVENTORY → no add/edit/transfer/delete).
    PERMISSIONS.VIEW_INVENTORY,
  ],

  [USER_ROLES.ACCOUNT_USER]: [
    PERMISSIONS.VIEW_ORDERS,
    PERMISSIONS.VIEW_PURCHASE,
    PERMISSIONS.VIEW_PURCHASE_CHALLAN,
    PERMISSIONS.VIEW_PURCHASE_DAYBOOK,
  ],

  [USER_ROLES.DELIVERY_SUPERVISOR]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_ORDERS,
    PERMISSIONS.VIEW_PURCHASE,
    PERMISSIONS.VIEW_PURCHASE_ADVICE,
    // Read-only inventory (no MANAGE_INVENTORY → no add/edit/transfer/delete).
    PERMISSIONS.VIEW_INVENTORY,
  ],

  [USER_ROLES.LABOUR]: [],
};

// OPERATIONS_MANAGER = strict union of Sales + Account + Delivery permissions,
// plus customer create/edit. VIEW_CUSTOMERS is the only addition over the union
// and exists solely to reach the Customers page/nav (the create/edit buttons are
// role-guarded — see rbac.canEdit + CustomersPage guard). Computed from the
// source arrays so it can't drift if those change.
ROLE_PERMISSIONS[USER_ROLES.OPERATIONS_MANAGER] = Array.from(new Set([
  ...ROLE_PERMISSIONS[USER_ROLES.SALES_STAFF],
  ...ROLE_PERMISSIONS[USER_ROLES.ACCOUNT_USER],
  ...ROLE_PERMISSIONS[USER_ROLES.DELIVERY_SUPERVISOR],
  PERMISSIONS.VIEW_CUSTOMERS,
]));

// Navigation Menu Items with Required Permissions
export const NAVIGATION_ITEMS = {
  DASHBOARD: {
    path: '/dashboard',
    label: 'Dashboard',
    icon: 'dashboard',
    requiredPermissions: [PERMISSIONS.VIEW_DASHBOARD],
  },
  USERS: {
    path: '/users',
    label: 'User Management',
    icon: 'users',
    requiredPermissions: [PERMISSIONS.VIEW_USERS],
  },
  IP_ADDRESSES: {
    path: '/ip-addresses',
    label: 'IP Addresses',
    icon: 'network',
    requiredPermissions: [PERMISSIONS.VIEW_IP_ADDRESSES],
  },
  INVENTORY: {
    path: '/inventory',
    label: 'Inventory',
    icon: 'inventory',
    requiredPermissions: [PERMISSIONS.VIEW_INVENTORY],
  },
  ORDERS: {
    path: '/orders',
    label: 'Orders',
    icon: 'orders',
    requiredPermissions: [PERMISSIONS.VIEW_ORDERS],
  },
  REPORTS: {
    path: '/reports',
    label: 'Reports',
    icon: 'reports',
    requiredPermissions: [PERMISSIONS.VIEW_REPORTS],
  },
  SETTINGS: {
    path: '/settings',
    label: 'Settings',
    icon: 'settings',
    requiredPermissions: [PERMISSIONS.VIEW_SETTINGS],
  },
  SYSTEM: {
    path: '/system',
    label: 'System Admin',
    icon: 'system',
    requiredPermissions: [PERMISSIONS.MANAGE_SYSTEM],
  },
  PURCHASE: {
    path: '/purchase',
    label: 'Purchase Orders',
    icon: 'shopping-cart',
    requiredPermissions: [PERMISSIONS.VIEW_PURCHASE],
  },
};

// App Configuration
export const APP_CONFIG = {
  REQUEST_TIMEOUT: Number(process.env.REACT_APP_REQUEST_TIMEOUT) || 100000, // 100 seconds
  TOKEN_EXPIRY_BUFFER: Number(process.env.REACT_APP_TOKEN_EXPIRY_BUFFER) || 300, // 5 minutes in seconds
};

// External services
export const NOMINATIM_SEARCH = process.env.REACT_APP_NOMINATIM_SEARCH || 'https://nominatim.openstreetmap.org/search';

// External Links
export const EXTERNAL_LINKS = {
  DEVELOPER_WEBSITE: process.env.REACT_APP_DEVELOPER_WEBSITE || 'https://suranaspg.in',
};